import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL;
const fmt = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const PackCapacityView = () => {
    const [loading, setLoading] = useState(true);
    const [packs, setPacks] = useState([]);
    const [tousProduitsDetails, setTousProduitsDetails] = useState([]);
    const [capacitesIndividuelles, setCapacitesIndividuelles] = useState([]);
    const [scenariosOptimises, setScenariosOptimises] = useState([]);
    const [previsionFinanciereStock, setPrevisionFinanciereStock] = useState(null);

    // ── System Mode Toggles ──────────────────────────────────────────────────
    const [analysisMode, setAnalysisMode] = useState('pack'); // 'pack' | 'produit'
    const [stockMode, setStockMode] = useState('net'); // 'net' | 'brut'

    // ── Mode A State : Analyse par Pack ──────────────────────────────────────
    const [selectedCatId, setSelectedCatId] = useState('');
    const [selectedPackId, setSelectedPackId] = useState('');
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [desiredPacksCount, setDesiredPacksCount] = useState(10);

    // ── Mode B State : Analyse par Produit (Recherche Inversée) ──────────────
    const [selectedSearchProductIds, setSelectedSearchProductIds] = useState([]);

    // ── Simulateur Manuel Multi-Packs ─────────────────────────────────────────
    const [panier, setPanier] = useState({}); // { packId: quantite }
    const [simulationResult, setSimulationResult] = useState(null);
    const [simulating, setSimulating] = useState(false);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const fetchCapacityData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/packs/capacity-simulation`, {
                headers: getAuthHeaders()
            });
            setPacks(res.data.packs || []);
            setTousProduitsDetails(res.data.tousProduitsDetails || []);
            setCapacitesIndividuelles(res.data.capacitesIndividuelles || []);
            setScenariosOptimises(res.data.scenariosOptimises || []);
            setPrevisionFinanciereStock(res.data.previsionFinanciereStock || null);

            // Default pack selection if available
            if (res.data.packs && res.data.packs.length > 0) {
                const firstPack = res.data.packs[0];
                setSelectedPackId(firstPack._id);
            }
        } catch (err) {
            console.error('Erreur fetchCapacityData:', err);
            Swal.fire('Erreur', 'Impossible de charger la capacité stock et prévisions.', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCapacityData();
    }, [fetchCapacityData]);

    // Fast product lookup map
    const productStockMap = useMemo(() => {
        const map = new Map();
        for (const p of tousProduitsDetails) {
            const pid = String(p._id);
            map.set(pid, p);
        }
        return map;
    }, [tousProduitsDetails]);

    // Unique Categories list from Packs
    const packCategories = useMemo(() => {
        const catMap = new Map();
        for (const p of packs) {
            for (const cat of (p.categories || [])) {
                if (cat.categorieId) {
                    const cid = String(cat.categorieId._id || cat.categorieId);
                    const label = cat.categorieId.label || cat.categorieId.nom || 'Catégorie';
                    catMap.set(cid, label);
                }
            }
        }
        return Array.from(catMap.entries()).map(([id, label]) => ({ id, label }));
    }, [packs]);

    // Filtered Packs list according to selected Category
    const filteredPacks = useMemo(() => {
        if (!selectedCatId) return packs;
        return packs.filter(p => (p.categories || []).some(cat => String(cat.categorieId?._id || cat.categorieId) === selectedCatId));
    }, [packs, selectedCatId]);

    // Current Active Pack Object
    const activePack = useMemo(() => {
        if (!selectedPackId) return null;
        return packs.find(p => String(p._id) === String(selectedPackId)) || null;
    }, [packs, selectedPackId]);

    // Flattened products of the active pack
    const activePackProducts = useMemo(() => {
        if (!activePack) return [];
        const items = [];
        for (const cat of (activePack.categories || [])) {
            for (const pComp of (cat.produits || [])) {
                if (!pComp.produitId) continue;
                const pid = String(pComp.produitId._id || pComp.produitId);
                const pInfo = productStockMap.get(pid) || pComp.produitId;
                const reqQty = Number(pComp.quantiteRecommandee) || 1;
                const stockVal = stockMode === 'net' ? (pInfo?.stockNetLibre ?? pInfo?.quantite ?? 0) : (pInfo?.stockPhysique ?? pInfo?.quantite ?? 0);
                const capIndiv = reqQty > 0 ? Math.floor(stockVal / reqQty) : 0;

                items.push({
                    productId: pid,
                    nomProduit: pInfo?.nomProduit || 'Produit',
                    reference: pInfo?.reference || 'N/A',
                    reqQty,
                    stockVal,
                    stockPhysique: pInfo?.stockPhysique ?? pInfo?.quantite ?? 0,
                    besoinEngage: pInfo?.besoinEngage ?? 0,
                    stockNetLibre: pInfo?.stockNetLibre ?? pInfo?.quantite ?? 0,
                    prixAchat: Number(pInfo?.prixAchat || pInfo?.prixMetre || 0),
                    capIndiv,
                    catLabel: cat.categorieId?.label || 'Composant'
                });
            }
        }
        return items;
    }, [activePack, productStockMap, stockMode]);

    // Sync selected checkboxes when active pack changes
    useEffect(() => {
        if (activePackProducts.length > 0) {
            setSelectedProductIds(new Set(activePackProducts.map(p => p.productId)));
        } else {
            setSelectedProductIds(new Set());
        }
    }, [selectedPackId, activePackProducts]);

    // ── Mode A Calculations ─────────────────────────────────────────────────
    const packAnalysisResult = useMemo(() => {
        if (activePackProducts.length === 0) return null;

        // 1. Capacity based on SELECTED products subset
        const selectedItems = activePackProducts.filter(p => selectedProductIds.has(p.productId));
        let capSelection = Infinity;
        let limitantSelection = null;

        for (const item of selectedItems) {
            if (item.capIndiv < capSelection) {
                capSelection = item.capIndiv;
                limitantSelection = item;
            }
        }
        if (capSelection === Infinity) capSelection = 0;

        // 2. Capacity based on ALL products (complete pack)
        let capComplete = Infinity;
        let limitantGlobal = null;

        for (const item of activePackProducts) {
            if (item.capIndiv < capComplete) {
                capComplete = item.capIndiv;
                limitantGlobal = item;
            }
        }
        if (capComplete === Infinity) capComplete = 0;

        const ecartPacks = Math.max(0, capSelection - capComplete);

        // 3. Scenario Matrix for N Packs
        const scenarioMatrix = activePackProducts.map(p => {
            const totalNeed = p.reqQty * desiredPacksCount;
            const remaining = p.stockVal - totalNeed;
            const isFeasible = remaining >= 0;
            return {
                ...p,
                totalNeed,
                remaining,
                missingQty: isFeasible ? 0 : Math.abs(remaining)
            };
        });

        const isScenarioFeasible = scenarioMatrix.every(item => item.remaining >= 0);

        // 4. Purchasing Helper: Unlock capacity up to lead selected component or desired packs
        const targetPacksForUnlock = desiredPacksCount > 0 ? desiredPacksCount : (capSelection > 0 ? capSelection : 10);
        const purchaseSuggestions = activePackProducts.map(p => {
            const targetNeed = p.reqQty * targetPacksForUnlock;
            const stockDispo = p.stockVal;
            const toBuy = Math.max(0, targetNeed - stockDispo);
            const estimatedCost = toBuy * p.prixAchat;
            return {
                ...p,
                targetNeed,
                stockDispo,
                toBuy,
                estimatedCost
            };
        }).filter(p => p.toBuy > 0);

        const totalPurchaseInvestment = purchaseSuggestions.reduce((acc, curr) => acc + curr.estimatedCost, 0);

        return {
            selectedItemsCount: selectedItems.length,
            totalItemsCount: activePackProducts.length,
            capSelection,
            limitantSelection,
            capComplete,
            limitantGlobal,
            ecartPacks,
            scenarioMatrix,
            isScenarioFeasible,
            targetPacksForUnlock,
            purchaseSuggestions,
            totalPurchaseInvestment
        };
    }, [activePackProducts, selectedProductIds, desiredPacksCount]);

    // ── Mode B Calculations (Produit → Packs Reverse Lookup) ───────────────────
    const reverseProductAnalysis = useMemo(() => {
        if (selectedSearchProductIds.length === 0) return [];

        const searchSet = new Set(selectedSearchProductIds);
        const results = [];

        for (const pack of packs) {
            const packItems = [];
            let hasAnySearchProduct = false;

            for (const cat of (pack.categories || [])) {
                for (const pComp of (cat.produits || [])) {
                    if (!pComp.produitId) continue;
                    const pid = String(pComp.produitId._id || pComp.produitId);
                    const pInfo = productStockMap.get(pid) || pComp.produitId;
                    const reqQty = Number(pComp.quantiteRecommandee) || 1;
                    const stockVal = stockMode === 'net' ? (pInfo?.stockNetLibre ?? pInfo?.quantite ?? 0) : (pInfo?.stockPhysique ?? pInfo?.quantite ?? 0);
                    const capIndiv = reqQty > 0 ? Math.floor(stockVal / reqQty) : 0;
                    const isSelectedInSearch = searchSet.has(pid);

                    if (isSelectedInSearch) hasAnySearchProduct = true;

                    packItems.push({
                        productId: pid,
                        nomProduit: pInfo?.nomProduit || 'Produit',
                        reqQty,
                        stockVal,
                        capIndiv,
                        isSelectedInSearch
                    });
                }
            }

            if (hasAnySearchProduct) {
                // Theoretical capacity based ONLY on search products present in this pack
                const searchItemsInPack = packItems.filter(i => i.isSelectedInSearch);
                let capTheoreticalSearch = Infinity;
                for (const item of searchItemsInPack) {
                    capTheoreticalSearch = Math.min(capTheoreticalSearch, item.capIndiv);
                }
                if (capTheoreticalSearch === Infinity) capTheoreticalSearch = 0;

                // Real capacity with ALL components
                let capRealComplete = Infinity;
                let limitantProd = null;
                for (const item of packItems) {
                    if (item.capIndiv < capRealComplete) {
                        capRealComplete = item.capIndiv;
                        limitantProd = item;
                    }
                }
                if (capRealComplete === Infinity) capRealComplete = 0;

                results.push({
                    packId: pack._id,
                    nomPack: pack.nom,
                    puissance: pack.puissance,
                    prixFinal: Number(pack.prixFinal || 0),
                    searchItemsInPack,
                    capTheoreticalSearch,
                    capRealComplete,
                    limitantProd
                });
            }
        }

        return results;
    }, [packs, selectedSearchProductIds, productStockMap, stockMode]);

    // Selection handlers
    const toggleProductSelection = (pid) => {
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            if (next.has(pid)) next.delete(pid);
            else next.add(pid);
            return next;
        });
    };

    const selectAllProducts = () => {
        setSelectedProductIds(new Set(activePackProducts.map(p => p.productId)));
    };

    const deselectAllProducts = () => {
        setSelectedProductIds(new Set());
    };

    const toggleSearchProductSelect = (pid) => {
        setSelectedSearchProductIds(prev => {
            if (prev.includes(pid)) return prev.filter(id => id !== pid);
            return [...prev, pid];
        });
    };

    const handlePanierQtyChange = (packId, qty) => {
        const parsed = Math.max(0, parseInt(qty, 10) || 0);
        setPanier(prev => ({ ...prev, [packId]: parsed }));
    };

    const runManualSimulation = async () => {
        const selected = Object.entries(panier)
            .filter(([_, q]) => q > 0)
            .map(([packId, quantite]) => ({ packId, quantite }));

        if (selected.length === 0) {
            Swal.fire('Info', 'Veuillez saisir au moins une quantité de pack pour lancer la simulation.', 'info');
            return;
        }

        setSimulating(true);
        try {
            const res = await axios.get(`${API_URL}/packs/capacity-simulation`, {
                headers: getAuthHeaders(),
                params: { scenario: JSON.stringify(selected) }
            });
            setSimulationResult(res.data.simulationManuelle || null);
        } catch (err) {
            console.error('Erreur runManualSimulation:', err);
            Swal.fire('Erreur', 'Erreur lors de l\'exécution du simulateur.', 'error');
        } finally {
            setSimulating(false);
        }
    };

    if (loading) {
        return <LoadingSpinner message="Chargement des simulations et capacités stock..." />;
    }

    return (
        <div className="pack-capacity-container">
            {/* ── Financial Stock Forecast Header Cards ──────────────────── */}
            <div className="capacity-kpi-grid">
                <div className="cap-kpi-card blue">
                    <div className="cap-kpi-icon">💰</div>
                    <div className="cap-kpi-content">
                        <h3>{fmt(previsionFinanciereStock?.valeurAchatStock)} DT</h3>
                        <p>Valeur d'Achat du Stock Actuel</p>
                    </div>
                </div>
                <div className="cap-kpi-card green">
                    <div className="cap-kpi-icon">📈</div>
                    <div className="cap-kpi-content">
                        <h3>{fmt(previsionFinanciereStock?.caPotentielStock)} DT</h3>
                        <p>CA Potentiel Estimé via Packs</p>
                    </div>
                </div>
                <div className="cap-kpi-card purple">
                    <div className="cap-kpi-icon">⚡</div>
                    <div className="cap-kpi-content">
                        <h3>{fmt(previsionFinanciereStock?.margePotentielleStock)} DT</h3>
                        <p>Marge Potentielle Estimée</p>
                    </div>
                </div>
            </div>

            {/* ── BARRE DE CONTRÔLE PRINCIPALE (MODES D'ANALYSE & STOCK) ──────── */}
            <div className="capacity-control-toolbar packs-filter-glass">
                <div className="mode-selector-group">
                    <span className="control-label">Mode d'Analyse :</span>
                    <button
                        className={`mode-btn ${analysisMode === 'pack' ? 'active' : ''}`}
                        onClick={() => setAnalysisMode('pack')}
                    >
                        📦 Analyse par Pack & Composants
                    </button>
                    <button
                        className={`mode-btn ${analysisMode === 'produit' ? 'active' : ''}`}
                        onClick={() => setAnalysisMode('produit')}
                    >
                        🧩 Analyse par Produit (Recherche Inversée)
                    </button>
                </div>

                <div className="stock-toggle-group">
                    <span className="control-label">Mode Stock Utilisé :</span>
                    <label className={`radio-pill ${stockMode === 'net' ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="stockMode"
                            value="net"
                            checked={stockMode === 'net'}
                            onChange={() => setStockMode('net')}
                        />
                        <span>🟢 Stock Net Libre (Stock physique - engagements non livrés)</span>
                    </label>
                    <label className={`radio-pill ${stockMode === 'brut' ? 'active' : ''}`}>
                        <input
                            type="radio"
                            name="stockMode"
                            value="brut"
                            checked={stockMode === 'brut'}
                            onChange={() => setStockMode('brut')}
                        />
                        <span>🔵 Stock Brut (Physique total en dépôt)</span>
                    </label>
                </div>
            </div>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODE A : ANALYSE PAR PACK & COMPOSANTS SÉLECTIONNÉS              */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {analysisMode === 'pack' && (
                <div className="analysis-mode-section">
                    {/* Zone A : Sélection Catégorie & Pack */}
                    <div className="capacity-section-card">
                        <div className="section-header">
                            <h2>🎯 Zone A — Sélection du Pack à Analyser</h2>
                            <p>Choisissez un pack et sélectionnez les produits à inclure dans la simulation.</p>
                        </div>
                        <div className="pack-selection-toolbar">
                            <div className="filter-field">
                                <label>1. Catégorie de Pack :</label>
                                <select
                                    value={selectedCatId}
                                    onChange={e => {
                                        setSelectedCatId(e.target.value);
                                        const matching = packs.filter(p => !e.target.value || (p.categories || []).some(c => String(c.categorieId?._id || c.categorieId) === e.target.value));
                                        if (matching.length > 0) setSelectedPackId(matching[0]._id);
                                    }}
                                >
                                    <option value="">Toutes les catégories</option>
                                    {packCategories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-field flex-grow">
                                <label>2. Pack Commercial :</label>
                                <select
                                    value={selectedPackId}
                                    onChange={e => setSelectedPackId(e.target.value)}
                                >
                                    {filteredPacks.map(p => (
                                        <option key={p._id} value={p._id}>{p.nom} ({p.puissance}) — {fmt(p.prixFinal)} DT</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Zone B : Grille des produits avec cases à cocher & capacites */}
                    {activePack && (
                        <div className="capacity-section-card">
                            <div className="section-header flex-between">
                                <div>
                                    <h2>📋 Zone B — Composants du Pack [{activePack.nom}] ({activePack.puissance})</h2>
                                    <p>Cochez ou décochez les produits pour calculer la capacité selon tout ou partie des composants.</p>
                                </div>
                                <div className="checklist-actions">
                                    <button className="btn-small-action" onClick={selectAllProducts}>☑ Tout sélectionner</button>
                                    <button className="btn-small-action" onClick={deselectAllProducts}>☐ Tout désélectionner</button>
                                </div>
                            </div>

                            <div className="products-checklist-grid">
                                {activePackProducts.map(p => {
                                    const isChecked = selectedProductIds.has(p.productId);
                                    return (
                                        <div
                                            key={p.productId}
                                            className={`checklist-item-card ${isChecked ? 'checked' : 'unchecked'}`}
                                            onClick={() => toggleProductSelection(p.productId)}
                                        >
                                            <div className="item-checkbox-header">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {}} // Handled by parent div click
                                                />
                                                <span className="item-cat-tag">{p.catLabel}</span>
                                            </div>
                                            <h4 className="item-title">{p.nomProduit}</h4>
                                            <span className="item-ref">Ref: {p.reference}</span>

                                            <div className="item-metrics-list">
                                                <div className="metric-row">
                                                    <span>Stock dispo ({stockMode === 'net' ? 'Net' : 'Brut'}) :</span>
                                                    <strong>{p.stockVal} pcs</strong>
                                                </div>
                                                <div className="metric-row">
                                                    <span>Requis / Pack :</span>
                                                    <strong>{p.reqQty} pcs</strong>
                                                </div>
                                                <div className="metric-row highlight">
                                                    <span>Capacité théorique :</span>
                                                    <span className={`capa-pill ${p.capIndiv > 0 ? 'green' : 'red'}`}>
                                                        {p.capIndiv} packs
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Zone C : Comparatif Capacité Sélection vs Complète & Goulot d'Étranglement */}
                    {packAnalysisResult && (
                        <div className="capacity-section-card result-highlights-card">
                            <div className="section-header">
                                <h2>📊 Zone C — Résultats & Goulots d'Étranglement</h2>
                            </div>

                            <div className="results-comparison-grid">
                                <div className="result-stat-box blue">
                                    <span className="stat-label">Capacité selon Sélection ({packAnalysisResult.selectedItemsCount}/{packAnalysisResult.totalItemsCount} produits)</span>
                                    <h3 className="stat-value">{packAnalysisResult.capSelection} Packs</h3>
                                    {packAnalysisResult.limitantSelection && (
                                        <span className="stat-hint">
                                            Limitée par : <strong>{packAnalysisResult.limitantSelection.nomProduit}</strong> ({packAnalysisResult.limitantSelection.stockVal} en stock)
                                        </span>
                                    )}
                                </div>

                                <div className="result-stat-box green">
                                    <span className="stat-label">Capacité Réelle Complète du Pack (Tous composants)</span>
                                    <h3 className="stat-value">{packAnalysisResult.capComplete} Packs</h3>
                                    {packAnalysisResult.limitantGlobal && (
                                        <span className="stat-hint warning">
                                            ⚠️ Goulot d'étranglement global : <strong>{packAnalysisResult.limitantGlobal.nomProduit}</strong> ({packAnalysisResult.limitantGlobal.stockVal} en stock)
                                        </span>
                                    )}
                                </div>

                                <div className="result-stat-box orange">
                                    <span className="stat-label">Écart Réalisable / Réel</span>
                                    <h3 className="stat-value">{packAnalysisResult.ecartPacks} Packs</h3>
                                    <span className="stat-hint">Différence débloquable en approvisionnant les composants manquants</span>
                                </div>
                            </div>

                            {packAnalysisResult.limitantSelection && packAnalysisResult.limitantGlobal && (
                                <div className="explanation-banner">
                                    <span className="banner-icon">💡</span>
                                    <p>
                                        Le stock de <strong>{packAnalysisResult.limitantSelection.nomProduit}</strong> permettrait d'assembler <strong>{packAnalysisResult.capSelection} packs</strong>,
                                        mais le stock global des composants restreint actuellement la capacité réelle à <strong>{packAnalysisResult.capComplete} packs complets</strong> (goulot d'étranglement : <strong>{packAnalysisResult.limitantGlobal.nomProduit}</strong>).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Zone D : Scenario Simulator pour N Packs Souhaités */}
                    {packAnalysisResult && (
                        <div className="capacity-section-card">
                            <div className="section-header flex-between">
                                <div>
                                    <h2>🧮 Zone D — Matrice de Consommation pour $N$ Packs Souhaités</h2>
                                    <p>Testez la faisabilité pour un objectif d'installations cible.</p>
                                </div>
                                <div className="desired-qty-box">
                                    <label htmlFor="desired-packs-input">Nombre de Packs souhaités :</label>
                                    <input
                                        id="desired-packs-input"
                                        type="number"
                                        min="1"
                                        value={desiredPacksCount}
                                        onChange={e => setDesiredPacksCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                    />
                                </div>
                            </div>

                            <div className="scenario-status-banner">
                                {packAnalysisResult.isScenarioFeasible ? (
                                    <span className="badge-status-large success">
                                        ✅ Scénario Réalisable : Le stock actuel est suffisant pour assembler {desiredPacksCount} × {activePack?.nom}.
                                    </span>
                                ) : (
                                    <span className="badge-status-large danger">
                                        ⚠️ Stock Insuffisant : Rupture détectée sur certains composants pour fabriquer {desiredPacksCount} × {activePack?.nom}.
                                    </span>
                                )}
                            </div>

                            <div className="table-responsive">
                                <table className="capacity-table">
                                    <thead>
                                        <tr>
                                            <th>PRODUIT</th>
                                            <th>CATÉGORIE</th>
                                            <th>QTÉ / PACK</th>
                                            <th>BESOIN POUR {desiredPacksCount} PACKS</th>
                                            <th>STOCK DISPO ({stockMode.toUpperCase()})</th>
                                            <th>SOLDE RESTANT / MANQUANT</th>
                                            <th>FAISABILITÉ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packAnalysisResult.scenarioMatrix.map(item => (
                                            <tr key={item.productId} className={item.remaining < 0 ? 'row-danger' : ''}>
                                                <td><strong>{item.nomProduit}</strong></td>
                                                <td>{item.catLabel}</td>
                                                <td className="text-center">{item.reqQty}</td>
                                                <td className="text-center"><strong>{item.totalNeed}</strong></td>
                                                <td className="text-center">{item.stockVal}</td>
                                                <td className="text-center">
                                                    <span className={`badge-capa ${item.remaining >= 0 ? 'green' : 'red'}`}>
                                                        {item.remaining >= 0 ? `+${item.remaining} restant` : `Manque ${item.missingQty}`}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    {item.remaining >= 0 ? '✅ OK' : '❌ Rupture'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Zone E : Aide à l'Achat Fournisseur / Déblocage de Capacité */}
                    {packAnalysisResult && packAnalysisResult.purchaseSuggestions.length > 0 && (
                        <div className="capacity-section-card purchasing-card">
                            <div className="section-header">
                                <h2>🛒 Zone E — Plan d'Achat & Déblocage de Capacité</h2>
                                <p>
                                    Produits supplémentaires à commander pour fabriquer <strong>{packAnalysisResult.targetPacksForUnlock} × {activePack?.nom}</strong> (objectif d'installations).
                                </p>
                            </div>

                            <div className="table-responsive">
                                <table className="capacity-table">
                                    <thead>
                                        <tr>
                                            <th>PRODUIT À COMMANDER</th>
                                            <th>BESOIN CIBLE</th>
                                            <th>STOCK ACTUEL</th>
                                            <th>QUANTITÉ À ACHETER</th>
                                            <th>PRIX ACHAT UNIT. (DT)</th>
                                            <th>COÛT ESTIMÉ D'ACHAT (DT)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packAnalysisResult.purchaseSuggestions.map(item => (
                                            <tr key={item.productId}>
                                                <td><strong>{item.nomProduit}</strong></td>
                                                <td className="text-center">{item.targetNeed}</td>
                                                <td className="text-center">{item.stockDispo}</td>
                                                <td className="text-center">
                                                    <span className="badge-missing">+{item.toBuy} unité(s)</span>
                                                </td>
                                                <td className="text-right">{fmt(item.prixAchat)} DT</td>
                                                <td className="text-right font-bold text-purple">{fmt(item.estimatedCost)} DT</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="purchasing-footer-summary">
                                <span>Investissement Total Estimé pour Déblocage :</span>
                                <strong>{fmt(packAnalysisResult.totalPurchaseInvestment)} DT</strong>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* MODE B : ANALYSE PAR PRODUIT (RECHERCHE INVERSÉE PRODUIT → PACKS)  */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {analysisMode === 'produit' && (
                <div className="analysis-mode-section">
                    <div className="capacity-section-card">
                        <div className="section-header">
                            <h2>🧩 Mode B — Recherche Inversée Produit(s) $\rightarrow$ Packs</h2>
                            <p>Sélectionnez un ou plusieurs produits pour identifier tous les packs qui les utilisent et comparer les capacités.</p>
                        </div>

                        <div className="search-products-selector">
                            <label>Sélectionner les produits à analyser :</label>
                            <div className="product-chips-grid">
                                {tousProduitsDetails.map(p => {
                                    const isSelected = selectedSearchProductIds.includes(String(p._id));
                                    const stockVal = stockMode === 'net' ? (p.stockNetLibre ?? p.quantite ?? 0) : (p.stockPhysique ?? p.quantite ?? 0);
                                    return (
                                        <button
                                            key={p._id}
                                            type="button"
                                            className={`chip-btn ${isSelected ? 'active' : ''}`}
                                            onClick={() => toggleSearchProductSelect(String(p._id))}
                                        >
                                            <span className="chip-name">{p.nomProduit}</span>
                                            <small className="chip-stock">Stock: {stockVal}</small>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Matrix of matching packs */}
                    <div className="capacity-section-card">
                        <div className="section-header">
                            <h2>📊 Packs Consommateurs des Produits Sélectionnés</h2>
                        </div>

                        {selectedSearchProductIds.length === 0 ? (
                            <div className="no-selection-box py-8 text-center">
                                <span className="sim-icon">🔍</span>
                                <p>Cliquez sur un ou plusieurs produits ci-dessus pour lancer la recherche d'opportunités de packs.</p>
                            </div>
                        ) : reverseProductAnalysis.length === 0 ? (
                            <div className="no-selection-box py-8 text-center">
                                <h3>Aucun Pack ne contient ces produits</h3>
                                <p>Aucun pack actif dans la base n'utilise la combinaison de produits sélectionnée.</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="capacity-table">
                                    <thead>
                                        <tr>
                                            <th>PACK COMMERCIAL</th>
                                            <th>PUISSANCE</th>
                                            <th>PRODUIT(S) REQUIS & DISPO</th>
                                            <th>CAPACITÉ SELON PRODUIT(S) SÉLECTIONNÉS</th>
                                            <th>CAPACITÉ RÉELLE PACK COMPLET</th>
                                            <th>GOULOT D'ÉTRANGLEMENT GLOBAL</th>
                                            <th>PRIX VENTE (DT)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reverseProductAnalysis.map(res => (
                                            <tr key={res.packId}>
                                                <td><strong>{res.nomPack}</strong></td>
                                                <td><span className="badge-puissance">{res.puissance}</span></td>
                                                <td>
                                                    <ul className="mini-prod-list">
                                                        {res.searchItemsInPack.map(item => (
                                                            <li key={item.productId}>
                                                                • {item.nomProduit} (Requis: {item.reqQty} | Stock: {item.stockVal})
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge-capa blue">
                                                        {res.capTheoreticalSearch} packs
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge-capa ${res.capRealComplete > 0 ? 'green' : 'orange'}`}>
                                                        {res.capRealComplete} packs
                                                    </span>
                                                </td>
                                                <td>
                                                    {res.limitantProd ? (
                                                        <span className="lim-name text-red">⚠️ {res.limitantProd.nomProduit} ({res.limitantProd.stockVal} en stock)</span>
                                                    ) : (
                                                        <span className="text-gray-400">Aucun</span>
                                                    )}
                                                </td>
                                                <td className="text-right font-bold">{fmt(res.prixFinal)} DT</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* VUES ET SECTIONS PAR DÉFAUT (CAPACITÉS INDIVIDUELLES ET SCÉNARIOS) */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <div className="capacity-section-card">
                <div className="section-header">
                    <h2>📊 Vue Synthétique Générale — Capacités par Pack</h2>
                </div>
                <div className="table-responsive">
                    <table className="capacity-table">
                        <thead>
                            <tr>
                                <th>PACK</th>
                                <th>PUISSANCE</th>
                                <th>CAPACITÉ BRUTE (STOCK DÉPÔT)</th>
                                <th>CAPACITÉ NETTE (POST-ENGAGÉS)</th>
                                <th>COMPOSANT LIMITANT (RUPTURE)</th>
                                <th>PRIX VENTE (DT)</th>
                                <th>MARGE / PACK (DT)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {capacitesIndividuelles.map(p => (
                                <tr key={p.packId}>
                                    <td><strong>{p.nomPack}</strong></td>
                                    <td><span className="badge-puissance">{p.puissance}</span></td>
                                    <td className="text-center">
                                        <span className={`badge-capa ${p.capaciteBrute > 0 ? 'green' : 'red'}`}>
                                            {p.capaciteBrute} pack(s)
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <span className={`badge-capa ${p.capaciteNette > 0 ? 'blue' : 'orange'}`}>
                                            {p.capaciteNette} pack(s)
                                        </span>
                                    </td>
                                    <td>
                                        {p.composantLimitantBrut ? (
                                            <div className="limitant-box">
                                                <span className="lim-name">⚠️ {p.composantLimitantBrut.nomProduit}</span>
                                                <small className="lim-details">Stock: {p.composantLimitantBrut.stockPhysique} | Requis: {p.composantLimitantBrut.besoinParPack}</small>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Aucun</span>
                                        )}
                                    </td>
                                    <td className="text-right">{fmt(p.prixFinal)} DT</td>
                                    <td className="text-right font-bold text-green">{fmt(p.margePack)} DT</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Simulateur de Scénarios Multi-Packs (Panier Manuel) ────── */}
            <div className="capacity-section-card simulator-card">
                <div className="section-header">
                    <h2>🧮 Simulateur de Scénarios Multi-Packs (Panier de Packs)</h2>
                    <p>Définissez un panier de packs pour tester instantanément la disponibilité du stock commun.</p>
                </div>

                <div className="simulator-grid">
                    <div className="simulator-inputs">
                        <h3>Saisir la quantité souhaitée par Pack :</h3>
                        <div className="pack-input-rows">
                            {capacitesIndividuelles.map(p => (
                                <div key={p.packId} className="pack-input-item">
                                    <label>{p.nomPack} ({p.puissance})</label>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="0"
                                        value={panier[p.packId] || ''}
                                        onChange={e => handlePanierQtyChange(p.packId, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                        <button
                            className="btn-simulate"
                            onClick={runManualSimulation}
                            disabled={simulating}
                        >
                            {simulating ? 'Calcul en cours...' : '🚀 Lancer la Simulation'}
                        </button>
                    </div>

                    <div className="simulator-output">
                        <h3>Résultat de la Simulation :</h3>
                        {!simulationResult ? (
                            <div className="no-simulation-box">
                                <span className="sim-icon">🎯</span>
                                <p>Saisissez des quantités et cliquez sur "Lancer la Simulation" pour afficher les résultats.</p>
                            </div>
                        ) : (
                            <div className={`simulation-result-card ${simulationResult.faisable ? 'success' : 'warning'}`}>
                                <div className="result-header">
                                    <span className="status-badge">
                                        {simulationResult.faisable ? '✅ SCÉNARIO FEASIBLE (STOCK SUFFISANT)' : '⚠️ STOCK INSUFFISANT / RUPTURES DÉTECTÉES'}
                                    </span>
                                </div>
                                <div className="result-kpis">
                                    <div className="res-kpi">
                                        <span>CA Potentiel</span>
                                        <strong>{fmt(simulationResult.caPotentiel)} DT</strong>
                                    </div>
                                    <div className="res-kpi">
                                        <span>Coût Produits</span>
                                        <strong>{fmt(simulationResult.coutTotal)} DT</strong>
                                    </div>
                                    <div className="res-kpi">
                                        <span>Marge Potentielle</span>
                                        <strong className="text-green">{fmt(simulationResult.margePotentielle)} DT</strong>
                                    </div>
                                </div>

                                {simulationResult.produitsManquants.length > 0 && (
                                    <div className="missing-products-list">
                                        <h4>Produits Manquants à Commander :</h4>
                                        <ul>
                                            {simulationResult.produitsManquants.map(m => (
                                                <li key={m.produitId}>
                                                    <strong>{m.nomProduit}</strong> — Requis : {m.quantiteRequise}, Dispo : {m.stockDisponible} $\rightarrow$ <span className="badge-missing">Manque {m.manquant} unité(s)</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Scénarios d'Optimisation Automatique (Combinatoire) ──────── */}
            <div className="capacity-section-card">
                <div className="section-header">
                    <h2>🤖 Scénarios d'Optimisation Automatique Multi-Packs</h2>
                    <p>Définitions combinatoires déterministes basées sur le stock physique disponible actuel.</p>
                </div>

                <div className="scenarios-grid">
                    {scenariosOptimises.map(scenar => (
                        <div key={scenar.typeScenar} className="scenar-card">
                            <h3>{scenar.titre}</h3>
                            <div className="scenar-metrics">
                                <div className="metric-item">
                                    <span>Installations Totales</span>
                                    <strong>{scenar.totalInstallations} packs</strong>
                                </div>
                                <div className="metric-item">
                                    <span>CA Potentiel</span>
                                    <strong className="text-blue">{fmt(scenar.caPotentiel)} DT</strong>
                                </div>
                                <div className="metric-item">
                                    <span>Marge Potentielle</span>
                                    <strong className="text-green">{fmt(scenar.margePotentielle)} DT</strong>
                                </div>
                            </div>

                            <div className="scenar-repartition">
                                <h4>Répartition Conseillée :</h4>
                                <ul>
                                    {scenar.repartition.length === 0 ? (
                                        <li className="no-rep">Aucune installation possible avec le stock actuel.</li>
                                    ) : (
                                        scenar.repartition.map(r => (
                                            <li key={r.packId}>
                                                <span>{r.nomPack}</span>
                                                <strong className="rep-qty">× {r.quantite}</strong>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PackCapacityView;
